import { useState } from 'react';
import { Play, Square } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useCurrentTimer, useStartTimer, useStopTimer } from '../queries';
import type { WorkItem } from '../types';
import { TimerElapsed } from './TimerElapsed';
import { TimerNoteDialog } from './TimerNoteDialog';

/** Start / stop the work-item timer (ADR-076). Reflects the global running timer
 *  so the button shows "Stop" when this very item is being tracked. Stopping —
 *  here, or by switching from a timer on another item — asks what was done
 *  first (DEV13-117). */
export function WorkItemTimerButton({ item }: { item: WorkItem }) {
  const { data: timer } = useCurrentTimer();
  const start = useStartTimer();
  const stop = useStopTimer();
  // The running timer the note is being asked for — a later timer must not
  // inherit an open dialog (see TimerIndicator).
  const [askingFor, setAskingFor] = useState<number | null>(null);
  const isThis = timer?.work_item === item.id;

  const noteDialog = timer ? (
    <TimerNoteDialog
      open={askingFor === timer.id}
      onOpenChange={(next) => setAskingFor(next ? timer.id : null)}
      timer={timer}
      mode={isThis ? 'stop' : 'switch'}
      pending={isThis ? stop.isPending : start.isPending}
      error={isThis ? stop.error : start.error}
      onConfirm={(note) => {
        const done = { onSuccess: () => setAskingFor(null) };
        if (isThis) stop.mutate({ description: note }, done);
        else start.mutate({ id: item.public_id, body: { previous_description: note } }, done);
      }}
    />
  ) : null;

  if (isThis) {
    return (
      <div className="ml-auto flex items-center gap-2">
        {timer?.started_at ? (
          <TimerElapsed
            startedAt={timer.started_at}
            className="bg-primary/10 rounded-md px-2 py-1 text-sm"
          />
        ) : null}
        <Button
          size="sm"
          className="h-8 bg-red-600 text-white shadow-sm hover:bg-red-700"
          disabled={stop.isPending}
          onClick={() => {
            stop.reset();
            setAskingFor(timer.id);
          }}
        >
          <Square className="size-3.5 fill-current" /> Stop timer
        </Button>
        {noteDialog}
      </div>
    );
  }
  return (
    <>
      <Button
        size="sm"
        className="ml-auto h-8 bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"
        disabled={start.isPending}
        onClick={() => {
          // A timer running elsewhere stops first — ask for its note then.
          if (timer) {
            start.reset();
            setAskingFor(timer.id);
          } else start.mutate({ id: item.public_id });
        }}
        title={timer ? 'Stops your current timer and starts one here' : 'Start a timer on this item'}
      >
        <Play className="size-3.5 fill-current" /> Start timer
      </Button>
      {noteDialog}
    </>
  );
}
